import {
  ImplementationKind,
  PlainImplementationKind,
  toPlainImplementationKindMap,
} from '@finch-api/common/dist/internal/connect/authorize';
import {
  PayrollProviderId,
  RegisterImplementationProps,
} from 'constants/types';
import RegisterGustoCredential from './Gusto/RegisterGustoCredential';
import RegisterRipplingApi from './Rippling/RegisterRipplingApi';
import SignInGusto from './Gusto/SignInGusto';
import SignInPaylocityApi from './Paylocity/SignInPaylocityApi';
import SignInPaylocityCredential from './Paylocity/SignInPaylocityCredential';
import SignInPaylocityOAuth from './Paylocity/SignInPaylocityOAuth';
import SignInJustworksCredential from './Justworks/SignInJustworksCredential';
import SignInSquareOAuth from './SquarePayroll/SignInSquareOAuth';
import SignInSquareCredential from './SquarePayroll/SignInSquareCredential';
import SignInSaplingApi from './Sapling/SignInSaplingApi';
import SignInSageHRApi from './SageHR/SignInSageHRApi';
import SignInSequoiaOneCredential from './SequoiaOne/SignInSequoiaOneCredential';
import SignInPaycomCredentialWithCheck from './Paycom/SignInPaycomCredentialWithCheck';
import SignInPaycomApi from './Paycom/SignInPaycomApi';
import { SignInAdpRunCredential } from './ADPRun/SignInAdpRunCredential';
import { SignInAdpWorkforceNowCredential } from './ADPWorkforceNow/SignInAdpWorkforceNowCredential';
import SignInWaveCredential from './Wave/SignInWaveCredential';
import SignInQuickbooksCredential from './Quickbooks/SignInQuickbooksCredential';
import SignInMiterApi from './Miter/SignInMiterApi';
import SignInRemoteApi from './Remote/SignInRemoteApi';
import SignInRemoteOAuth from './Remote/SignInRemoteOAuth';
import SignInWarpApi from './Warp/SignInWarpApi';
import SignInZenefitsCredential from './Zenefits/SignInZenefitsCredential';
import SignInUltiProApiMultiStep from './UltiPro/SignInUltiProApiMultiStep';
import SignInUKGReadyApiMultiStep from './UKGReady/SignInUKGReadyApiMultiStep';
import SignInTriNetApi from './TriNet/SignInTriNetApi';
import SignInWorkdayCredential from './Workday/SignInWorkdayCredential';
import SignInWorkdayApiMultiStep from './Workday/SignInWorkdayApiMultiStep';
import { SignInAlphaStaffApi } from './AlphaStaff/SignInAlphaStaffApi';
import { SignInBambooHrCredential } from './BambooHR/SignInBambooHrCredential';
import { SignInBambooHrOAuth } from './BambooHR/SignInBambooHrOAuth';
import { SignInBobApi } from './Bob/SignInBobApi';
import { SignInBobCredential } from './Bob/SignInBobCredential';
import SignInBobOAuth from './Bob/SignInBobOAuth';
import SignInHumaansApi from './Humaans/SignInHumaansApi';
import SignInHorizonPayrollCredential from './HorizonPayroll/SignInHorizonPayrollCredential';
import SignInNamelyCredential from './Namely/SignInNamelyCredential';
import SignInNamelyApi from './Namely/SignInNamelyApi';
import SignInInsperityCredential from './Insperity/SignInInsperityCredential';
import SignInInsperityApi from './Insperity/SignInInsperityAPI';
import SignInIsolvedApi from './ISolved/SignInIsolvedApi';
import { SignInIsolvedCredential } from './ISolved/SignInIsolvedCredential';
import SignInPaychexFlexCredential from './PaychexFlex/SignInPaychexFlexCredential';
import SignInPaychexFlexApi from './PaychexFlex/SignInPaychexFlexApi';
import SignInPaycorOAuth from './Paycor/SignInPaycorOAuth';
import SignInCeridianDayforceApi from './CeridianDayforce/SignInCeridianDayforceApi';
import { SignInCeridianDayforceCredential } from './CeridianDayforce/SignInCeridianDayforceCredential';
import { SignInCoAdvantageCredential } from './CoAdvantage/SignInCoAdvantageCredential';
import SignInPersonioApi from './Personio/SignInPersonioApi';
import SignInOysterHrOAuth from './OysterHR/SignInOysterHrOAuth';
import SignInAppliedBusinessSolutionsApi from './AppliedBusinessSolutions/SignInAppliedBusinessSolutionsApi';
import SignInHybridApi from './Hybrid/SignInHybridApi';
import SignInFinchCredential from './Finch/SignInFinchCredential';
import SignInFactorialHROAuth from './FactorialHR/SignInFactorialHROAuth';
import SignInDeelOAuth from './Deel/SignInDeelOAuth';
import SignInCounterpointHcmApi from './CounterpointHcm/SignInCounterpointHcmApi';
import SignInRipplingOAuth from './Rippling/SignInRipplingOAuth';
import SignInLatticeOAuth from './Lattice/SignInLatticeOAuth';
import SignInOnpayCredential from './Onpay/SignInOnpayCredential';
import SignInZenefitsApi from './Zenefits/SignInZenefitsApi';
import SignInJustworksOAuth from './Justworks/SignInJustworksOAuth';
import { SignInPaydayPayrollCredential } from './PaydayPayroll/SignInPaydayPayrollCredential';
import SignInProxusHrApi from './ProxusHR/SignInProxusHrApi';
import SignInSesameApi from './Sesame/SignInSesameApi';
import { SignInECCACredential } from './ECCA/SignInECCACredential';
import SignInEveryOAuth from './Every/SignInEveryOAuth';
import { SignInHeartlandCredential } from './Heartland/SignInHeartlandCredential';
import { SignInWorkforceCredential } from './Workforce/SignInWorkforceCredential';
import { SignInAccountantsWorldCredential } from './AccountantsWorld/SignInAccountantsWorldCredential';

const providerToComponentMap: {
  [k in PayrollProviderId]: Partial<{
    [k in PlainImplementationKind]:
      | ((props: RegisterImplementationProps) => JSX.Element)
      | null;
  }>;
} = {
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.AAP_ISOLVED]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.ADP_RUN]: {
    [ImplementationKind.CREDENTIAL]: SignInAdpRunCredential,
  },
  [PayrollProviderId.ADP_WORKFORCE_NOW]: {
    [ImplementationKind.CREDENTIAL]: SignInAdpWorkforceNowCredential,
  },
  [PayrollProviderId.ACCOUNTANTSWORLD]: {
    [ImplementationKind.CREDENTIAL]: SignInAccountantsWorldCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.AHOLA]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.ALPHASTAFF]: {
    [ImplementationKind.API_TOKEN]: SignInAlphaStaffApi,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.AMCHECK]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.APPLIED_BUSINESS_SOLUTIONS]: {
    [ImplementationKind.API_TOKEN]: SignInAppliedBusinessSolutionsApi,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.ASPEN_HR]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.BAMBOO_HR]: {
    [ImplementationKind.CREDENTIAL]: SignInBambooHrCredential,
    [ImplementationKind.API_TOKEN]: SignInBambooHrOAuth,
    [ImplementationKind.OAUTH]: SignInBambooHrOAuth,
  },
  [PayrollProviderId.BOB]: {
    [ImplementationKind.API_TOKEN]: SignInBobApi,
    [ImplementationKind.CREDENTIAL]: SignInBobCredential,
    [ImplementationKind.OAUTH]: SignInBobOAuth,
  },
  [PayrollProviderId.CERIDIAN_DAYFORCE]: {
    [ImplementationKind.CREDENTIAL]: SignInCeridianDayforceCredential,
    [ImplementationKind.API_TOKEN]: SignInCeridianDayforceApi,
  },
  [PayrollProviderId.COADVANTAGE]: {
    [ImplementationKind.CREDENTIAL]: SignInCoAdvantageCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.COASTAL_PAYROLL]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.COMMON_SENSE_PAYROLL]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.COUNTERPOINT_HCM]: {
    [ImplementationKind.API_TOKEN]: SignInCounterpointHcmApi,
  },
  [PayrollProviderId.DEEL]: {
    [ImplementationKind.API_TOKEN]: SignInDeelOAuth,
    [ImplementationKind.OAUTH]: SignInDeelOAuth,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.DOMINION_PAYROLL]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.ECCA]: {
    [ImplementationKind.CREDENTIAL]: SignInECCACredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.EMPLOYDRIVE]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  // every.io's implementation is registered as api_token (the shared partner
  // key drives the data sync), but the employer-facing sign-in is the OAuth-
  // style consent handshake: no credential is ever typed into Connect.
  [PayrollProviderId.EVERY_IO]: {
    [ImplementationKind.API_TOKEN]: SignInEveryOAuth,
  },
  [PayrollProviderId.FACTORIAL_HR]: {
    [ImplementationKind.OAUTH]: SignInFactorialHROAuth,
  },
  [PayrollProviderId.FINCH]: {
    [ImplementationKind.CREDENTIAL]: SignInFinchCredential,
  },
  [PayrollProviderId.GUSTO]: {
    [ImplementationKind.CREDENTIAL]: RegisterGustoCredential,
    [ImplementationKind.API_TOKEN]: null,
    [ImplementationKind.OAUTH]: SignInGusto,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.HEARTLAND]: {
    [ImplementationKind.CREDENTIAL]: SignInHeartlandCredential,
  },
  [PayrollProviderId.HORIZON_PAYROLL]: {
    [ImplementationKind.CREDENTIAL]: SignInHorizonPayrollCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.HR_BUTLER]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.HUMAANS]: {
    [ImplementationKind.API_TOKEN]: SignInHumaansApi,
  },
  [PayrollProviderId.HYBRID]: {
    [ImplementationKind.API_TOKEN]: SignInHybridApi,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.IBUSINESS_SOLUTIONS]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.INSPERITY]: {
    [ImplementationKind.CREDENTIAL]: SignInInsperityCredential,
    [ImplementationKind.API_TOKEN]: SignInInsperityApi,
    [ImplementationKind.OAUTH]: SignInInsperityApi,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.INTEGRATED_PAYROLL_SERVICES]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.ISOLVED]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
    [ImplementationKind.API_TOKEN]: SignInIsolvedApi,
  },
  [PayrollProviderId.JUSTWORKS]: {
    [ImplementationKind.CREDENTIAL]: SignInJustworksCredential,
    [ImplementationKind.API_TOKEN]: null,
    [ImplementationKind.OAUTH]: SignInJustworksOAuth,
    [ImplementationKind.ASSISTED]: null,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.KABEL]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.LATTICE]: {
    [ImplementationKind.OAUTH]: SignInLatticeOAuth,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.LIFT_HCM]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.MITER]: {
    [ImplementationKind.API_TOKEN]: SignInMiterApi,
  },
  [PayrollProviderId.NAMELY]: {
    [ImplementationKind.CREDENTIAL]: SignInNamelyCredential,
    [ImplementationKind.API_TOKEN]: SignInNamelyApi,
    [ImplementationKind.OAUTH]: SignInNamelyApi,
  },
  [PayrollProviderId.ONPAY]: {
    [ImplementationKind.CREDENTIAL]: SignInOnpayCredential,
    [ImplementationKind.API_TOKEN]: null,
    [ImplementationKind.OAUTH]: null,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.OYSTER_HR]: {
    [ImplementationKind.OAUTH]: SignInOysterHrOAuth,
  },
  [PayrollProviderId.PAYCHEX_FLEX]: {
    [ImplementationKind.CREDENTIAL]: SignInPaychexFlexCredential,
    [ImplementationKind.API_TOKEN]: SignInPaychexFlexApi,
    [ImplementationKind.OAUTH]: SignInPaychexFlexApi,
  },
  [PayrollProviderId.PAYCOM]: {
    [ImplementationKind.CREDENTIAL]: SignInPaycomCredentialWithCheck,
    [ImplementationKind.API_TOKEN]: SignInPaycomApi,
    [ImplementationKind.OAUTH]: SignInPaycomApi,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.PAYCOR]: {
    [ImplementationKind.API_TOKEN]: SignInPaycorOAuth,
    [ImplementationKind.OAUTH]: SignInPaycorOAuth,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.PAYFLUENCE]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.PAYLOCITY]: {
    [ImplementationKind.CREDENTIAL]: SignInPaylocityCredential,
    [ImplementationKind.API_TOKEN]: SignInPaylocityApi,
    [ImplementationKind.OAUTH]: SignInPaylocityOAuth,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.PAYDAY_PAYROLL]: {
    [ImplementationKind.CREDENTIAL]: SignInPaydayPayrollCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.PAYROLL_NETWORK]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.PAYROLL_SPECIALTIES]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.PAYROLL_VAULT]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.PERSONIO]: {
    [ImplementationKind.API_TOKEN]: SignInPersonioApi,
    [ImplementationKind.OAUTH]: SignInPersonioApi,
  },
  [PayrollProviderId.PROXUS_HR]: {
    [ImplementationKind.API_TOKEN]: SignInProxusHrApi,
    // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
    // credential sign-in component.
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.QFORCE]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.QUICKBOOKS]: {
    [ImplementationKind.CREDENTIAL]: SignInQuickbooksCredential,
  },
  [PayrollProviderId.REMOTE]: {
    [ImplementationKind.API_TOKEN]: SignInRemoteApi,
    [ImplementationKind.OAUTH]: SignInRemoteOAuth,
  },
  [PayrollProviderId.RIPPLING]: {
    [ImplementationKind.OAUTH]: SignInRipplingOAuth,
    [ImplementationKind.API_TOKEN]: RegisterRipplingApi,
  },
  [PayrollProviderId.SAGE_HR]: {
    [ImplementationKind.CREDENTIAL]: null,
    [ImplementationKind.API_TOKEN]: SignInSageHRApi,
    [ImplementationKind.OAUTH]: SignInSageHRApi,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.SAPLING]: {
    [ImplementationKind.CREDENTIAL]: null,
    [ImplementationKind.API_TOKEN]: SignInSaplingApi,
    [ImplementationKind.OAUTH]: SignInSaplingApi,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.SESAME]: {
    [ImplementationKind.API_TOKEN]: SignInSesameApi,
  },
  [PayrollProviderId.SEQUOIA_ONE]: {
    [ImplementationKind.CREDENTIAL]: SignInSequoiaOneCredential,
    [ImplementationKind.API_TOKEN]: null,
    [ImplementationKind.OAUTH]: null,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.SQUARE_PAYROLL]: {
    [ImplementationKind.CREDENTIAL]: SignInSquareCredential,
    [ImplementationKind.API_TOKEN]: SignInSquareOAuth,
    [ImplementationKind.OAUTH]: SignInSquareOAuth,
    [ImplementationKind.ASSISTED]: null,
  },
  // Whitelabel of isolved on the same myisolved infra, so it reuses isolved's
  // credential sign-in component.
  [PayrollProviderId.THREAD_HCM]: {
    [ImplementationKind.CREDENTIAL]: SignInIsolvedCredential,
  },
  [PayrollProviderId.TRINET]: {
    [ImplementationKind.CREDENTIAL]: null,
    [ImplementationKind.API_TOKEN]: SignInTriNetApi,
    [ImplementationKind.OAUTH]: SignInTriNetApi,
    [ImplementationKind.ASSISTED]: null,
  },
  [PayrollProviderId.UKG_READY]: {
    [ImplementationKind.API_TOKEN]: SignInUKGReadyApiMultiStep,
  },
  [PayrollProviderId.ULTI_PRO]: {
    [ImplementationKind.API_TOKEN]: SignInUltiProApiMultiStep,
    [ImplementationKind.OAUTH]: SignInUltiProApiMultiStep,
  },
  [PayrollProviderId.WARP]: {
    [ImplementationKind.API_TOKEN]: SignInWarpApi,
  },
  [PayrollProviderId.WAVE]: {
    [ImplementationKind.CREDENTIAL]: SignInWaveCredential,
  },
  [PayrollProviderId.WORKDAY]: {
    [ImplementationKind.CREDENTIAL]: SignInWorkdayCredential,
    [ImplementationKind.API_TOKEN]: SignInWorkdayApiMultiStep,
  },
  [PayrollProviderId.WORKFORCECOM]: {
    [ImplementationKind.CREDENTIAL]: SignInWorkforceCredential,
  },
  [PayrollProviderId.ZENEFITS]: {
    [ImplementationKind.CREDENTIAL]: SignInZenefitsCredential,
    [ImplementationKind.API_TOKEN]: SignInZenefitsApi,
  },
};

export const getProviderComponent = (
  payrollProviderId: PayrollProviderId,
  implementationKind: ImplementationKind,
) => {
  const rawImplementationKind =
    toPlainImplementationKindMap[implementationKind];

  return providerToComponentMap[payrollProviderId]?.[rawImplementationKind];
};
